import { useState, useEffect } from 'react';
import configurationService from '../services/configurationService';
import { Configuration, ConfigurationCategoryType } from '../types';

/**
 * Hook to fetch configurations by category
 */
export const useConfiguration = (category: ConfigurationCategoryType, includeInactive = false) => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await configurationService.getConfigurationsByCategory(category, includeInactive);
        
        if (result.success && Array.isArray(result.data)) {
          setConfigurations(result.data);
        } else {
          setError(result.message || 'Failed to fetch configurations');
        }
      } catch (error) {
        console.error('Error fetching configurations:', error);
        setError('Failed to fetch configurations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigurations();
  }, [category, includeInactive]);

  return { configurations, isLoading, error, refetch: () => {
    setIsLoading(true);
    setError(null);
    const fetchConfigurations = async () => {
      try {
        const result = await configurationService.getConfigurationsByCategory(category, includeInactive);
        
        if (result.success && Array.isArray(result.data)) {
          setConfigurations(result.data);
        } else {
          setError(result.message || 'Failed to fetch configurations');
        }
      } catch (error) {
        console.error('Error fetching configurations:', error);
        setError('Failed to fetch configurations');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfigurations();
  }};
};

/**
 * Hook to get configurations formatted for select/dropdown components
 */
export const useConfigurationOptions = (category: ConfigurationCategoryType, includeInactive = false) => {
  const { configurations, isLoading, error, refetch } = useConfiguration(category, includeInactive);
  
  const options = configurations.map(config => ({
    value: config.value,
    label: config.label,
    key: config.key,
    description: config.description,
    disabled: !config.isActive
  }));

  return { options, isLoading, error, refetch };
};

/**
 * Hook to get a specific configuration value
 */
export const useConfigurationValue = (category: ConfigurationCategoryType, key: string) => {
  const [value, setValue] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await configurationService.getConfiguration(category, key);
        
        if (result.success && result.data) {
          setValue(result.data.value);
        } else {
          setError(result.message || 'Configuration not found');
        }
      } catch (error) {
        console.error('Error fetching configuration:', error);
        setError('Failed to fetch configuration');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfiguration();
  }, [category, key]);

  return { value, isLoading, error };
};
